// The period filter of every change log — the wireframe's `FiltruPerioada`
// (components/FiltruPerioada.tsx), which exists so "what changed last week" and
// "what changed between X and Y" can be asked wherever there is a history.
//
// Five presets and a custom interval, in the wireframe's own order. The register
// had the interval alone: two bare date fields, so the common questions each cost
// two date pickers and the arithmetic to fill them.
//
// Pure, and dated from an argument rather than from the clock, so a preset can be
// tested without freezing time.
// per docs/ui-shape-geography.md §The period filter

export type HistoryPeriodId = '' | 'last7' | 'last30' | 'month' | 'previousMonth' | 'custom';

export interface HistoryPeriod {
  id: HistoryPeriodId;
  /** ISO `YYYY-MM-DD`, empty when the period is open at that end. */
  from: string;
  to: string;
}

export const HISTORY_PERIOD_IDS: readonly HistoryPeriodId[] = [
  '',
  'last7',
  'last30',
  'month',
  'previousMonth',
  'custom',
];

export const EMPTY_PERIOD: HistoryPeriod = { id: '', from: '', to: '' };

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Today, as the presets date from. Injectable so a preset is testable. */
export function todayIso(now: Date = new Date()): string {
  return iso(now);
}

function addDays(today: string, days: number): string {
  const date = new Date(`${today}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return iso(date);
}

/**
 * The interval a chosen period stands for. `custom` carries its own dates; every
 * other id derives from today, so nothing has to be stored when the preset is
 * picked and a log left open overnight answers for the new day.
 */
export function periodRange(period: HistoryPeriod, today: string): { from: string; to: string } {
  switch (period.id) {
    case 'last7':
      // Seven days INCLUDING today, which is what "the last 7 days" means to a
      // reader looking at a log (the wireframe counts the same way: `-6`).
      return { from: addDays(today, -6), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'month':
      return { from: `${today.slice(0, 8)}01`, to: today };
    case 'previousMonth': {
      const year = Number(today.slice(0, 4));
      const month = Number(today.slice(5, 7));
      const previousYear = month === 1 ? year - 1 : year;
      const previousMonth = month === 1 ? 12 : month - 1;
      const padded = String(previousMonth).padStart(2, '0');
      // Day 0 of a month is the last day of the one before it.
      const lastDay = new Date(Date.UTC(previousYear, previousMonth, 0)).getUTCDate();
      return {
        from: `${previousYear}-${padded}-01`,
        to: `${previousYear}-${padded}-${String(lastDay).padStart(2, '0')}`,
      };
    }
    case 'custom':
      return { from: period.from, to: period.to };
    default:
      return { from: '', to: '' };
  }
}

/**
 * Whether a custom interval reads forwards. An interval with only one end filled
 * is still being typed, and is not wrong yet.
 */
export function isPeriodOrdered(period: HistoryPeriod): boolean {
  if (period.id !== 'custom') return true;
  if (!period.from || !period.to) return true;
  return period.from <= period.to;
}

/**
 * Whether the period is worth sending and worth drawing a chip for. A custom
 * interval with neither end chosen filters nothing, and one that reads backwards
 * would return nothing at all — the wireframe draws no chip for either.
 */
export function isPeriodApplied(period: HistoryPeriod): boolean {
  if (!period.id) return false;
  if (period.id !== 'custom') return true;
  return Boolean(period.from || period.to) && isPeriodOrdered(period);
}
